import Phaser from 'phaser';
import { GameScene } from '../GameScene';
import { ColyseusClient } from '../../network/ColyseusClient';
import { ClientMessageType } from '@shared/types/messages';
import { DEFAULT_MAP_ID } from '@shared/constants';

/**
 * Reconexión y teardown de render: crea el overlay de "Reconectando…", detecta la caída de conexión
 * (onLeave/onError/WS close), limpia TODOS los objetos de render de la sesión anterior
 * (clearAllRenderObjects), reintenta reconectar con backoff y reubica la cámara al reingresar; si
 * agota los intentos, redirige al login. Extraído de GameScene (god-object). El estado de reconexión
 * (reconnectOverlay/reconnectText/isReconnecting/reconnectTimeout) vive en GameScene (host) y se
 * accede vía `this.scene.*`.
 */
export class ReconnectionController {
  constructor(private scene: GameScene) {}

  public createReconnectOverlay(): void {
    const { width, height } = this.scene.scale;

    // Overlay negro similar al del menú

    this.scene.reconnectOverlay = this.scene.add

      .rectangle(0, 0, width, height, 0x000000, 0.8)

      .setOrigin(0, 0)

      .setDepth(2000)

      .setScrollFactor(0)

      .setVisible(false);

    // Texto de reconexión

    this.scene.reconnectText = this.scene.addText(width / 2, height / 2, 'Intentando reconectar con el servidor', {
        fontSize: '18px',

        color: '#ffffff',
      })

      .setOrigin(0.5)

      .setDepth(2001)

      .setScrollFactor(0)

      .setVisible(false);
  }

  public setupReconnectionHandlers(): void {
    if (!this.scene.room) return;

    // Detectar cuando se pierde la conexión

    this.scene.room.onLeave((code: number) => {
      // Code 1000 = cierre normal, no mostrar overlay

      if (code === 1000) return;

      this.handleDisconnection();
    });

    // Detectar errores de conexión

    this.scene.room.onError((code: number, message?: string) => {
      this.handleDisconnection();
    });

    // También escuchar eventos del WebSocket directamente

    const anyRoom = this.scene.room as any;

    const ws = anyRoom.connection?.transport?.ws as WebSocket | undefined;

    if (ws) {
      ws.addEventListener('close', () => {
        // Verificar si el WebSocket se cerró inesperadamente

        if (ws.readyState === WebSocket.CLOSED && !this.scene.isReconnecting) {
          this.handleDisconnection();
        }
      });

      ws.addEventListener('error', () => {
        if (!this.scene.isReconnecting) {
          this.handleDisconnection();
        }
      });
    }
  }

  public handleDisconnection(): void {
    if (this.scene.isReconnecting) return; // Ya estamos manejando la desconexión

    this.scene.isReconnecting = true;

    // Limpiar todos los objetos de renderizado de la sesión anterior

    this.clearAllRenderObjects();

    // Mostrar overlay de reconexión

    if (this.scene.reconnectOverlay) {
      this.scene.reconnectOverlay.setVisible(true);
    }

    if (this.scene.reconnectText) {
      this.scene.reconnectText.setVisible(true);
    }

    // Intentar reconectar

    this.attemptReconnection();
  }

  /**

   * Limpia todos los objetos de renderizado (jugadores, NPCs, chat bubbles, etc.)

   * Útil cuando se pierde la conexión y se va a reconectar

   */

  public clearAllRenderObjects(): void {
    // Limpiar todos los jugadores

    this.scene.playerObjects.forEach((render, id) => {
      render.sprite.destroy();

      this.scene.shadows.destroyCastShadow(render.castShadow);

      render.head?.destroy();

      this.scene.labels.destroyEntityLabel(render.label);

      render.arrow.destroy();

      render.shield?.destroy();

      render.weapon?.destroy();

      render.cameraTarget?.destroy();

      // Destruir animaciones activas

      for (const anim of render.activeAnimations) {
        anim.sprite.destroy();
      }

      render.activeAnimations = [];
      
      // Remove player light source
      this.scene.entityLight.removeEntityLight(id);

      this.scene.playerObjects.delete(id);
    });

    // Limpiar todos los NPCs

    this.scene.npcObjects.forEach((render, id) => {
      render.sprite.destroy();

      this.scene.shadows.destroyCastShadow(render.castShadow);

      render.head?.destroy();

      render.shield?.destroy();

      render.weapon?.destroy();

      this.scene.labels.destroyEntityLabel(render.label);

      // Remove NPC light source
      this.scene.entityLight.removeEntityLight(`npc_${id}`);

      this.scene.npcObjects.delete(id);
    });

    // Limpiar todos los chat bubbles

    this.scene.activeChatBubbles.forEach((bubble) => {
      bubble.destroy();
    });

    this.scene.activeChatBubbles.clear();

    this.scene.activeNpcChatBubbles.forEach((bubble) => {
      bubble.destroy();
    });

    this.scene.activeNpcChatBubbles.clear();

    // Limpiar items en el piso

    this.scene.groundItemObjects.forEach((obj) => {
      obj.sprite.destroy();

      obj.label.destroy();

      obj.effect?.destroy();
      obj.enchantEffect?.destroy();
    });

    this.scene.groundItemObjects.clear();

    // Destroy lighting debug panel
    this.scene.lightingDebug.destroyLightingDebugPanel();

    // Detener el seguimiento de la cámara antes de resetear

    this.scene.cameras.main.stopFollow();

    // Resetear el sprite del jugador local

    this.scene.localPlayerFollowTarget = null;

    this.scene.localSessionId = null;

    console.log('[GameScene] All render objects cleared due to disconnection');
  }

  /**

   * Reposiciona la cámara para seguir al jugador local

   * Útil después de una reconexión

   */

  public repositionCameraToLocalPlayer(): void {
    if (!this.scene.localSessionId) {
      return; // No hay jugador local aún
    }

    // Buscar el sprite del jugador local

    const localPlayerRender = this.scene.playerObjects.get(this.scene.localSessionId);

    if (!localPlayerRender) {
      return; // No se ha creado aún el render del jugador local
    }

    // Stop any existing follow (we use direct camera control now)
    this.scene.cameras.main.stopFollow();

    // Track the target for updateCameraPosition()
    this.scene.localPlayerFollowTarget = localPlayerRender.cameraTarget ?? localPlayerRender.sprite;

    // Immediately center camera on player (direct control, no follow)
    this.scene.cameraCtl.updateCameraPosition();

    console.log(
      '[GameScene] Camera repositioned to local player at',
      localPlayerRender.currentWorldX,
      localPlayerRender.currentWorldY
    );
  }

  public async attemptReconnection(): Promise<void> {
    if (!this.scene.colyseusClient || !this.scene.configData) return;

    // Timeout de 5 segundos

    this.scene.reconnectTimeout = this.scene.time.delayedCall(5000, () => {
      if (this.scene.isReconnecting) {
        // No se pudo reconectar, redirigir al login

        this.redirectToLogin();
      }
    });

    // Intentar reconectar en un loop hasta que funcione o se agote el tiempo

    const maxAttempts = 10;

    let attempts = 0;

    while (this.scene.isReconnecting && attempts < maxAttempts) {
      try {
        // Limpiar objetos de renderizado de la sesión anterior ANTES de reconectar

        this.clearAllRenderObjects();

        // Cerrar conexión anterior si existe

        if (this.scene.room) {
          this.scene.room.leave();
        }

        this.scene.room = null;

        this.scene.colyseusClient = null;

        // Hacer EXACTAMENTE lo mismo que en create() cuando se conecta desde el listado

        this.scene.colyseusClient = new ColyseusClient(this.scene.configData.serverUrl);

        this.scene.room = await this.scene.colyseusClient.joinGameRoom({
          characterId: this.scene.configData.characterId,

          map: DEFAULT_MAP_ID,
        });

        this.scene.currentMapId = DEFAULT_MAP_ID;

        this.scene.localSessionId = this.scene.room.sessionId;

        // Cancelar el timeout

        if (this.scene.reconnectTimeout) {
          this.scene.reconnectTimeout.destroy();

          this.scene.reconnectTimeout = null;
        }

        // Ocultar overlay

        this.scene.isReconnecting = false;

        if (this.scene.reconnectOverlay) {
          this.scene.reconnectOverlay.setVisible(false);
        }

        if (this.scene.reconnectText) {
          this.scene.reconnectText.setVisible(false);
        }

        // Reconfigurar handlers EXACTAMENTE como en create()

        // La cámara se configurará automáticamente cuando setupStateListeners()

        // procese al jugador local en addOrUpdatePlayer()

        this.scene.setupStateListeners();

        this.scene.setupServerMessageHandlers();

        this.setupReconnectionHandlers();

        // Solicitar items en el piso después de configurar los handlers

        // Usar un pequeño delay para asegurar que el servidor esté completamente listo

        // Esto asegura que los items se sincronicen correctamente después de la reconexión

        this.scene.time.delayedCall(100, () => {
          if (this.scene.colyseusClient && this.scene.colyseusClient.isConnected()) {
            console.log('[GameScene] Requesting ground items after reconnection');

            this.scene.colyseusClient.send({
              type: ClientMessageType.REQUEST_GROUND_ITEMS,
            } as never);
          }
        });

        // Reconfigurar input del teclado EXACTAMENTE como en create()

        // Esto asegura que los controles funcionen inmediatamente después de la reconexión

        this.scene.cursors = this.scene.input.keyboard!.createCursorKeys();
        this.scene.configureActionKeys();

        // Limpiar estado de input fantasma de la sesión anterior: si se perdió
        // un keyup durante la desconexión, la tecla quedaría "apretada" y el
        // jugador auto-caminaría apenas reconecta. Además reseteamos el
        // rate-limit del move para arrancar con una ventana de 200ms limpia.
        this.scene.fullscreen.resetMovementInput();
        this.scene.lastMoveTimestamp = performance.now();

        // Asegurar que el input del teclado esté habilitado

        if (this.scene.input.keyboard) {
          this.scene.input.keyboard.enabled = true;
        }

        // Dar focus al canvas para que capture eventos del teclado automáticamente

        // Usar setTimeout para asegurar que se ejecute después de que el overlay se oculte

        this.scene.time.delayedCall(50, () => {
          if (this.scene.game.canvas) {
            this.scene.game.canvas.focus();
          }
        });

        console.log('[GameScene] Reconnection successful, scene reinitialized like initial connection');

        return; // Reconexión exitosa
      } catch (error) {
        attempts++;

        console.error('[GameScene] Reconnection attempt', attempts, 'failed:', error);

        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 500));
        }
      }
    }

    // Si llegamos aquí, no se pudo reconectar

    // El timeout se encargará de redirigir

    console.error('[GameScene] Reconnection failed after', attempts, 'attempts');
  }

  public redirectToLogin(): void {
    // Limpiar sesión y redirigir al login

    // Eliminar token de sesión si existe

    const rememberKey = 'aureum_login_remember';

    try {
      localStorage.removeItem(rememberKey);
    } catch {
      // Ignorar errores de localStorage
    }

    // Redirigir al login recargando la página (volverá a mostrar AuthUI)

    window.location.href = window.location.origin + window.location.pathname;
  }
}
